import { createSelector } from 'reselect';

import type { DriverWithLastLocation } from '@cobliteam/common-types';

import { type Driver, mapWebData } from '@cobliteam/api-lib';
import type { DeviceFragment } from '@cobliteam/api-lib/src/__generated__/graphql';

import type { AppState } from '../AppState';
import { selectDevices } from '../Devices';

export const selectDrivers = (state: AppState) => state.drivers.driversWebData;
export const selectDriverFormData = (state: AppState) => state.drivers.driverFormData;
export const selectDeleteDriverStatus = (state: AppState) => state.drivers.deleteDriverWebData;
export const selectTokenAssociations = (state: AppState) => state.drivers.tokenAssociationsWebData;
export const selectTokenValidation = (state: AppState) => state.drivers.tokenValidationWebData;
export const selectSaveTokenAssociationStatus = (state: AppState) => state.drivers.saveTokenAssociationWebData;
export const selectGenerateDriverAppCodeStatus = (state: AppState) => state.drivers.generateDriverAppCodeWebData;
export const selectSaveDriverStatus = (state: AppState) => state.drivers.saveDriverWebData;

const getLength = (drivers: Driver[]): number => {
  return drivers.length;
};

export const selectNumberDrivers = createSelector(selectDrivers, mapWebData(getLength));

export const selectDriversWithLastLocation = createSelector(
  selectDrivers,
  selectDevices,
  mapWebData((drivers: Driver[], devices: DeviceFragment[]) => {
    // hashing devices by driver id, if possible
    const deviceDriverMap: {
      [key: string]: DeviceFragment;
    } = {};
    devices.forEach((device) => {
      const { vehicle } = device;
      if (vehicle?.driver?.id) {
        deviceDriverMap[vehicle.driver.id] = device;
      }
    });

    const deviceWithLastLocation: DriverWithLastLocation[] = drivers.map((driver) => {
      if (deviceDriverMap[driver.id]) {
        return {
          ...driver,
          lastLocation: deviceDriverMap[driver.id].lastLocation || undefined,
        };
      }

      return driver;
    });

    return deviceWithLastLocation;
  })
);

export const selectDriversById = createSelector(
  selectDrivers,
  mapWebData((drivers) => new Map(drivers.map((driver) => [driver.id, driver])))
);
